import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Globe, CheckSquare, BarChart2, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LogoWhiteMark } from '@/components/brand';
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, type SupportedLanguage } from '@/i18n';

interface OperatorShellProps {
  userName: string;
  notificationCount?: number;
}

export function OperatorShell({ userName, notificationCount = 0 }: OperatorShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('operator');

  // Language cycling
  const currentLanguage = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage;
  const currentIndex = SUPPORTED_LANGUAGES.indexOf(currentLanguage);
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;
  const nextIndex = (safeIndex + 1) % SUPPORTED_LANGUAGES.length;
  const nextLang = SUPPORTED_LANGUAGES[nextIndex] as SupportedLanguage;

  const handleLanguageChange = () => {
    i18n.changeLanguage(nextLang);
  };

  const navItems = [
    { path: '/tasks', labelKey: 'navigation.tasks', icon: CheckSquare },
    { path: '/stats', labelKey: 'navigation.stats', icon: BarChart2 },
    { path: '/profile', labelKey: 'navigation.profile', icon: User },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-neutral-light/30">
      {/* Header */}
      <header className="h-18 bg-primary px-4 flex items-center justify-between pt-safe">
        <div className="flex items-center gap-3">
          <LogoWhiteMark className="h-8 w-8" />
          <span className="text-white text-xl font-semibold">
            {t('shell.greeting', { name: userName })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button className="relative p-2 text-white/80 hover:text-white">
            <Bell className="h-6 w-6" />
            {notificationCount > 0 && (
              <span className="absolute top-1 right-1 h-4 w-4 bg-error rounded-full text-xs text-white flex items-center justify-center">
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </button>
          <button
            onClick={handleLanguageChange}
            className="p-2 text-white/80 hover:text-white flex items-center gap-1"
            title={`Switch to ${LANGUAGE_NAMES[nextLang]}`}
          >
            <Globe className="h-5 w-5" />
            <span className="text-xs font-medium">{LANGUAGE_NAMES[currentLanguage]}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-24">
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-20 bg-white border-t border-neutral-light pb-safe">
        <div className="h-full flex items-center justify-around px-4">
          {navItems.map(item => {
            const isActive = location.pathname.startsWith(item.path);
            const Icon = item.icon;

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center gap-1"
              >
                <div
                  className={cn(
                    'p-2 rounded-lg transition-colors',
                    isActive ? 'bg-primary/10 text-primary' : 'text-neutral-medium'
                  )}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <span
                  className={cn(
                    'text-xs font-medium',
                    isActive ? 'text-primary' : 'text-neutral-medium'
                  )}
                >
                  {t(item.labelKey)}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
